import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SxProps, Theme, Typography } from '@mui/material';

export type InfoAlertSeverity = 'info' | 'warning';

// The palette is flat on purpose. An earlier version put the icon colour inside a nested
// '& svg' selector in both the base and the severity object, and merging them with a shallow
// spread dropped the base's icon margins at every existing call site. Keeping the colour as a
// plain token means there is only ever one '& svg' block to build.
const severityPalette: Record<
  InfoAlertSeverity,
  { backgroundColor: string; borderColor: string; iconColor: string }
> = {
  info: { backgroundColor: '#D8EFFE', borderColor: '#8BC6FD', iconColor: '#3E8FFA' },
  warning: { backgroundColor: '#FFE4D8', borderColor: '#FF9A8B', iconColor: '#FF3F49' },
};

const noticeStyles = (severity: InfoAlertSeverity): SxProps<Theme> => {
  const { backgroundColor, borderColor, iconColor } = severityPalette[severity];
  return {
    display: 'flex',
    alignItems: 'center',
    borderWidth: '1px 1px 1px 5px',
    borderStyle: 'solid',
    borderRadius: '12px',
    padding: '8px',
    backgroundColor,
    borderColor,
    '& svg': {
      color: iconColor,
      marginRight: '9px',
      marginLeft: '9px',
    },
  };
};

interface InfoAlertProps {
  message: string | JSX.Element;
  /** Defaults to 'info' so the existing call sites keep their appearance. */
  severity?: InfoAlertSeverity;
  /**
   * Optional stable hook for tests and QA. Without it the only way to select this banner is
   * its translated text, which breaks whenever copy changes. No default, so the existing call
   * sites emit no attribute at all.
   */
  testId?: string;
}

export const InfoAlert = ({ message, severity = 'info', testId }: InfoAlertProps) => {
  return (
    <Typography
      data-testid={testId}
      sx={noticeStyles(severity)}
    >
      <ErrorOutlineIcon fontSize='small' />
      {message}
    </Typography>
  );
};
